import type { FC } from "react";
import { useTranslation } from "@privacy/translations";
import { HasPermission } from "../../shared/permissions";
import { DynamicGrid } from "../../dynamicGrid";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { PageTitle } from "../../shared/Page";
import {
  RegistrationMetadataFields,
  IS_SHARED_FROM_CUSTOMER_FIELD,
  RegistrationProcessorMetadataFields,
} from "../../api/registrations.types";
import { useAssistantAccess } from "../../shared/assistantAccess";
import { ControllerFilterPicker } from "./ControllerFilter";
import { CreateRegistrationDialog } from "./CreateRegistrationDialog";
import { CreateRegistrationMenuButton } from "./CreateRegistrationMenuButton";
import { useRegistrationVariant } from "./RegistrationVariantProvider";
import { prefetch } from "../../api/prefetch";
import { myTenantsHierarchyWithCreateRegistrationPermissionQueryOptions } from "../../api/registrations";

export const RegistrationToolbar: FC = () => {
  const variant = useRegistrationVariant();
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const hasAssistantAccess = useAssistantAccess();
  const onCreated = (id: string) => navigate(paths.registrationsInline({ id, variant }));

  return (
    <DynamicGrid.Toolbar
      pageTitle={
        <PageTitle
          title={t(`RegistrationGrid.${variant}.Title`)}
          subTitle={t(`RegistrationGrid.${variant}.SubTitle`)}
        />
      }
      actionButtons={
        <>
          <DynamicGrid.ExportButton
            exportPdfConfig={{
              formType: variant === "Controller" ? "privacyRegistration" : "privacyProcessorRegistration",
              metadataFields:
                variant === "Controller" ? RegistrationMetadataFields : RegistrationProcessorMetadataFields,
              excludedFields: [IS_SHARED_FROM_CUSTOMER_FIELD],
            }}
          />

          <HasPermission permission="nova-forms-api:registration:create">
            {hasAssistantAccess && variant === "Controller" ? (
              <CreateRegistrationMenuButton onCreated={onCreated} />
            ) : (
              <DynamicGrid.CreateNewButton
                labelKey="CreateRegistration.Button"
                onHover={() => void prefetch(myTenantsHierarchyWithCreateRegistrationPermissionQueryOptions())}
                renderDialog={({ onClose }) => <CreateRegistrationDialog onClose={onClose} onCreated={onCreated} />}
              />
            )}
          </HasPermission>
        </>
      }
      filterButtons={
        <>
          <ControllerFilterPicker />
          <DynamicGrid.ColumnFilterButton />
          <DynamicGrid.FilterButton />
        </>
      }
    />
  );
};
